import {
  Button,
  Checkbox,
  Flex,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Spacer,
  Stack,
} from "@chakra-ui/react";
import { useState } from "react";
import MDSpinner from "react-md-spinner";

import { RanaModal } from "@nens/ranalib";
import { CostTypeEnum, FloodMonthEnum } from "@nens/threedi/openapi-client";

import { getSimulationsApi } from "../../apiConfiguration";

const simulationsApi = getSimulationsApi();

interface SimulationPostProcessModalProps {
  isOpen: boolean;
  onClose(): void;
  onOpen(): void;
  simulationPk?: number;
  simulationName?: string;
}

export default function SimulationPostProcessModal({
  isOpen,
  onClose,
  simulationPk,
  simulationName,
}: SimulationPostProcessModalProps) {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const monthsLower: FloodMonthEnum[] = [
    "jan",
    "feb",
    "mar",
    "apr",
    "may",
    "jun",
    "jul",
    "aug",
    "sep",
    "oct",
    "nov",
    "dec",
  ];
  const repairTimeValues: Record<number, string> = {
    1: "0 hour",
    6: "6 hours",
    24: "1 day",
    48: "2 days",
    120: "5 days",
    240: "10 days",
  };

  const [rawDataAndLogging, setRawDataAndLogging] = useState(false);
  const [processBasicResults, setProcessBasicResults] = useState(false);
  const [arrivalTimeMap, setArrivalTimeMap] = useState(false);
  const [damageEstimation, setDamageEstimation] = useState(false);

  const [damageEstimationCostType, setDamageEstimationCostType] = useState<CostTypeEnum>("min");
  const [damageEstimateFloodMonth, setDamageEstimationFloodMonth] = useState<FloodMonthEnum>(
    monthsLower[0]
  );
  const [damageEstimationInundationHours, setDamageEstimationInundationHours] = useState(1);
  const [damageEstimationRepairTimeBuildings, setDamageEstimationRepairTimeBuildings] = useState(1);
  const [damageEstimationRepairTimeInfrastructure, setDamageEstimationRepairTimeInfrastructure] =
    useState(1);

  const [exporting, setExporting] = useState(false);

  const storeResults = async () => {
    if (!simulationPk) return;
    try {
      await simulationsApi.simulationsResultsPostProcessingLizardBasicDelete({
        simulationPk: simulationPk,
      });
    } catch (error) {
      console.log(error);
    }
    if (rawDataAndLogging && processBasicResults) {
      try {
        await simulationsApi.simulationsResultsPostProcessingLizardBasicCreate({
          simulationPk: simulationPk,
          data: {
            processBasicResults: true,
            scenarioName: simulationName,
          },
        });
      } catch (error) {
        console.log(error);
      }
    } else if (rawDataAndLogging) {
      try {
        await simulationsApi.simulationsResultsPostProcessingLizardBasicCreate({
          simulationPk: simulationPk,
          data: {
            processBasicResults: false,
            scenarioName: simulationName,
          },
        });
      } catch (error) {
        console.log(error);
      }
    }

    if (processBasicResults && arrivalTimeMap) {
      try {
        await simulationsApi.simulationsResultsPostProcessingLizardArrivalCreate({
          simulationPk: simulationPk,
          data: {},
        });
      } catch (error) {
        console.log(error);
      }
      setExporting(false);
    }

    if (processBasicResults && damageEstimation) {
      try {
        await simulationsApi.simulationsResultsPostProcessingLizardDamageCreate({
          simulationPk: simulationPk,
          data: {
            costType: damageEstimationCostType,
            floodMonth: damageEstimateFloodMonth,
            inundationPeriod: damageEstimationInundationHours,
            repairTimeInfrastructure: damageEstimationRepairTimeInfrastructure,
            repairTimeBuildings: damageEstimationRepairTimeBuildings,
          },
        });
      } catch (error) {
        console.log(error);
      }
    }

    try {
      await simulationsApi.simulationsResultsPostProcessingLizardStartCreate({
        simulationPk: simulationPk,
        data: {
          scenarioName: simulationName,
        },
      });
    } catch (error) {
      console.log(error);
    }
    onClose();
  };
  return (
    <RanaModal isOpen={isOpen} onClose={onClose} topBar="Post-process results">
      <Checkbox
        isChecked={rawDataAndLogging}
        onChange={() => setRawDataAndLogging(!rawDataAndLogging)}
      >
        Raw data and logging
      </Checkbox>
      <br />
      <Checkbox
        isChecked={rawDataAndLogging ? processBasicResults : false}
        onChange={() => setProcessBasicResults(!processBasicResults)}
        isDisabled={!rawDataAndLogging}
      >
        Basic processed results
      </Checkbox>
      <Stack pl={6} mt={1} spacing={1}>
        <Checkbox
          isChecked={rawDataAndLogging && processBasicResults ? arrivalTimeMap : false}
          onChange={() => setArrivalTimeMap(!arrivalTimeMap)}
          isDisabled={!(rawDataAndLogging && processBasicResults)}
        >
          Arrival time map
        </Checkbox>
        <Checkbox
          isChecked={rawDataAndLogging && processBasicResults ? damageEstimation : false}
          onChange={() => setDamageEstimation(!damageEstimation)}
          isDisabled={!(rawDataAndLogging && processBasicResults)}
        >
          Damage estimation
        </Checkbox>
        {rawDataAndLogging && processBasicResults && damageEstimation && (
          <>
            <br />
            Cost type:
            <br />
            <Select variant="outline">
              <option value="min" onClick={() => setDamageEstimationCostType("min")}>
                Min
              </option>
              <option value="average" onClick={() => setDamageEstimationCostType("avg")}>
                Average
              </option>
              <option value="max" onClick={() => setDamageEstimationCostType("max")}>
                Max
              </option>
            </Select>
            <br />
            Flood month
            <Select variant="outline">
              {months.map((month, idx) => (
                <option
                  value={month}
                  onClick={() => setDamageEstimationFloodMonth(monthsLower[idx])}
                >
                  {month}
                </option>
              ))}
            </Select>
            <br />
            Time of inundation (hours):
            <NumberInput
              min={1}
              defaultValue={1}
              onChange={(value) => setDamageEstimationInundationHours(Number(value))}
              value={damageEstimationInundationHours}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            <br />
            Repair time of buildings
            <Select
              variant="outline"
              defaultValue={"0_hour"}
              value={damageEstimationRepairTimeBuildings}
            >
              {Object.keys(repairTimeValues).map((key: string) => (
                <option
                  value={key}
                  onClick={() => setDamageEstimationRepairTimeBuildings(Number(key))}
                >
                  {repairTimeValues[Number(key)]}
                </option>
              ))}
            </Select>
            <br />
            Repair time of infrastructure
            <Select
              variant="outline"
              defaultValue={"0_hour"}
              value={damageEstimationRepairTimeInfrastructure}
            >
              {Object.keys(repairTimeValues).map((key: string) => (
                <option
                  value={key}
                  onClick={() => setDamageEstimationRepairTimeInfrastructure(Number(key))}
                >
                  {repairTimeValues[Number(key)]}
                </option>
              ))}
            </Select>
          </>
        )}
      </Stack>
      <Flex my={4}>
        <Button
          variant="secondary"
          onClick={() => {
            setExporting(false);
            onClose();
          }}
        >
          Cancel
        </Button>
        <Spacer />
        {exporting ? (
          <MDSpinner size={24} />
        ) : (
          <Button
            variant="primary"
            onClick={() => {
              setExporting(true);
              storeResults();
            }}
            isDisabled={!rawDataAndLogging}
          >
            Store Results
          </Button>
        )}
      </Flex>
    </RanaModal>
  );
}
